<!--
 Import statment for Select
 import Select from '@/components/Core/Form/Select.vue'
-->
<template>
  <FloatLabel :variant="variant">
    <Select
      ref="selectRef"
      :id="id"
      v-model="selection"
      :disabled="disabled"
      :filter="filter"
      :highlightOnSelect="true"
      :options="options"
      :optionLabel="optionLabel"
      :optionValue="optionValue"
      :placeholder="placeholder"
      :size="size"
      :showClear="showClear"
      :virtualScrollerOptions="virtualScrollerOptions"
      @hide="selectOverlayHidden"
      @change="value => emit('change', value)"
      :pt="{
        root: ({ state, props }) => ({
          class: [
            // Size
            'w-full',

            // Color and Background
            {'border-iris-600 ring-iris-300/40 dark:ring-iris-400/40' : state.focused},
            'placeholder:text-charcoal-700 dark:placeholder:text-charcoal-400',
            { 'bg-charcoal-100 dark:bg-charcoal' : filled }, // If filled prop is added to the Select

            { 'ring-2': !minimal && state.focused },

            // Minimal State
            { 'border-b shadow-none rounded-none border-t-0 border-l-0 border-r-0 ring-0 bg-transparent' : minimal },


            // Disabled State
            { 'cursor-not-allowed pointer-events-auto opacity-70': props.disabled },
            { 'bg-transparent' : props.disabled && minimal },
            { ' bg-white dark:bg-charcoal-900' : props.disabled && !minimal },

            // Setting Changed and Error border and ring colors
            setInputStateBorderAndRingColor(state.focused),

            // Misc
            'transition-none'
          ]
        }),
        overlay: {
          class: [
            // Color and Background
            { 'bg-white dark:bg-charcoal-900' : !filled },
            { 'bg-charcoal-100 dark:bg-charcoal' : filled }, // If filled prop is added to the Select
            'border-charcoal-200 dark:border-charcoal-800'
          ]
        },

        option: ({ context }) => ({
          class: [
            'group',

            // Font
            'text-sm',

            // Color and Background
            { 'text-charcoal-800 dark:text-charcoal-200' : !context.focused},
            { 'bg-iris-600 text-white': context.focused },
            { 'bg-iris-200/70 dark:bg-iris-400/80 text-inherit': context.selected },

            // Misc
            'truncate'
          ]
        }),
        optionCheckIcon: {
          class: [
            // Color
            'text-iris-500 dark:text-iris-300 group-hover:text-white'
          ]
        }
      }"
    >
      <!-- This is the Header/Input section. Either slot or default -->
      <template #value="{ value }">
        <slot name="input" :value="value"/>
        <slot />
      </template>

      <!-- This is the Options dropdown section. Either slot or default. -->
      <template #option="{ option }">
        <slot name="options" :option="option"/>
        <slot />
      </template>

    </Select>

    <!-- Float Label label -->
    <label v-if="label">{{ label }}</label>
  </FloatLabel>
</template>

<script setup>
import { ref } from 'vue'
import FloatLabel from 'primevue/floatlabel'
import Select from 'primevue/select'

const props = defineProps({
  // Setting the Yellow border/ring around the input
  changed: {
    type: Boolean
  },
  disabled: {
    type: Boolean
  },
  // Setting the Red border/ring around the input
  error: {
    type: Boolean
  },
  // Turning on contract for the Select
  filled: {
    type: Boolean
  },
  // Turning on the Filtering feature of the list of options
  filter: {
    type: Boolean
  },
  id: {
    type: String
  },
  // Used in conjunction with variant prop for Float Label
  label: {
    type: String
  },
  // For a border bottom instead of entire border
  minimal: {
    type: Boolean
  },
  // Label to display in the dropdown
  optionLabel: {
    type: String,
    default: 'label'
  },
  // Label value when label is selected
  optionValue: {
    type: String,
    default: 'value'
  },
  // Array of options in dropdown
  options: {
    type: Array
  },
  placeholder: {
    type: String
  },
  // Show clear button to clear out value in input
  showClear: {
    type: Boolean
  },
  // Controls the padding and text size of the input
  size: {
    type: String,
    validator: (val) => ['small', 'large'].includes(val),
    default: null
  },
  // Used for the Float Label
  variant: {
    type: String,
    validator: (val) => ['in', 'on', 'over'].includes(val),
    default: null
  },
  virtualScrollerOptions: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['change'])

const selection = defineModel()
const selectRef = ref(null)

function setInputStateBorderAndRingColor(focused) {
  if (!focused) {
    // If minimal input styling and not focused
    if (props.minimal) {
      if (props.error) {
        return 'border-red-500 ring-red-500/40'
      }
      else if (props.changed) {
        return 'border-yellow-500 ring-yellow-500/40'
      }
    }
    // If input has errors styling and not focused
    else if (props.error) {
      return 'border-red-500 ring-2 ring-red-500/40'
    }
    // If input is changed styling and not focused
    else if (props.changed) {
      return 'border-yellow-500 ring-2 ring-yellow-500/40'
    }

    // Default border for when Select has focus verse not focused
    return 'border-charcoal-200 dark:border-charcoal-800'
  }
}

function selectOverlayHidden() {
  /**
    To handle the border and ring correctly, the below blur() function finds the floating label wrapper
    and removes focus. This ensures that the iris border and ring are displayed correctly the next time
    the element is focused, even if it is in an error or changed state. Without this, the element never
    fully loses focus, causing it to retain its previous color when refocused rather than resetting and
    applying the correct focused styles.
  */

  selectRef.value?.$parentInstance.$el?.querySelector('span').blur()
}
</script>